// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngAppProvider from "@stoneage/App/Components/XngAppProvider/XngAppProvider";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { ReactNode } from "react";
import { css } from "styled-components";
import ExternalScripts from "@stoneage/App/Components/Common/ExternalScripts";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
}

export interface PageLayoutStyles {
    page?: InjectableCss;
    // XNG-Changes
    container?: InjectableCss;
}

export const styles: PageLayoutStyles = {
    page: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 20px;
            margin-top: 12px;
            ${xScreen("lte", "sm")} {
                position: static !important;
            }
            @media print {
                display: none !important;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            min-height: 100vh;

            // XNG-Change override the style of delayedSpinner as it has a 250px height
            &&& > div:first-child {
                height: 97px;
                ${xScreen("lte", "sm")} {
                    height: 88px;
                }
            }
            @media print {
                display: block !important;
            }
        `,
    },
};

// This doesn't currently support HMR
const PageLayout = ({ showHeader, header, showBreadcrumbs, pageContent, showFooter, footer }: PageLayoutProps) => {
    return (
        <StyledWrapper {...styles.container}>
            {/* XNG-Changes: add XngAppProvider */}
            <XngAppProvider>
                {showHeader && header}
                {showBreadcrumbs && (
                    <Page as="div" {...styles.page}>
                        <PageBreadcrumbs />
                    </Page>
                )}
                {pageContent}
                {showFooter && footer}
            </XngAppProvider>
            <ExternalScripts />
        </StyledWrapper>
    );
};

export default PageLayout;
